import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  type ConnectorPlatform,
  type ConnectorStatus,
  type MediaConnector,
  type SyncCadence,
  type SyncJobStatusResponse,
  CONNECTOR_PLATFORM_ICONS,
  CONNECTOR_PLATFORM_LABELS,
  mediaConnectorsQueryOptions,
  syncStatusQueryOptions,
} from '@/entities/media-connector';
import { type AddAccountSubmitPayload, useOAuthPopup } from '@/features/media-connectors';
import { ROUTES } from '@/shared/config/routes';
import { track } from '@/shared/analytics';
import type { UseMediaConnectorDetailResult } from './useMediaConnectorDetail.types';
import { useMediaConnectorMutations } from './useMediaConnectorMutations';

/**
 * Queries, mutations, filters and dialog state behind the Paid Media & Ad
 * Intelligence detail page. The page component stays presentational; every
 * side effect and derived value lives here.
 *
 * `platform` is a parameter rather than a `useParams` read so there is one
 * source of truth: callers that already resolved the platform (the rail's
 * active item, the route param) pass it in and cannot disagree with it. Call
 * this only once a platform is known to be valid and enabled — see
 * `useMediaConnectorAvailability`, which answers that without mounting the
 * account list or the sync-status poll this hook starts.
 */
export function useMediaConnectorDetail(
  platform: ConnectorPlatform,
): UseMediaConnectorDetailResult {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<MediaConnector | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<MediaConnector | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConnectorStatus | null>(null);

  const { data, isPending } = useQuery(mediaConnectorsQueryOptions());
  const { data: syncStatusData } = useQuery(syncStatusQueryOptions());
  const { initiateOAuth, connectingPlatform } = useOAuthPopup();

  const syncStatusMap = useMemo(() => {
    const map = new Map<string, SyncJobStatusResponse>();
    for (const job of syncStatusData?.items ?? []) {
      map.set(job.connectorId, job);
    }
    return map;
  }, [syncStatusData]);

  const accounts: MediaConnector[] = data?.items.filter((c) => c.platform === platform) ?? [];

  const {
    testMutation,
    triggerSyncMutation,
    disconnectMutation,
    updateMutation,
    connectTokenMutation,
    connectServiceAccountMutation,
  } = useMediaConnectorMutations({
    platform,
    accounts,
    onDeleted: () => setDeletingAccount(null),
    onUpdated: () => setEditingAccount(null),
    onConnected: () => setDialogOpen(false),
  });

  const filteredAccounts = useMemo(() => {
    let result = accounts;

    if (statusFilter) {
      result = result.filter((a) => a.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.externalAccountName?.toLowerCase().includes(q) ||
          a.tags.some((tag) => tag.toLowerCase().includes(q)),
      );
    }

    return result;
  }, [accounts, search, statusFilter]);

  const platformLabel = CONNECTOR_PLATFORM_LABELS[platform];
  const platformIcon = CONNECTOR_PLATFORM_ICONS[platform];

  const handleAddAccount = (payload: AddAccountSubmitPayload) => {
    track('media_connector_connect_initiated', {
      platform,
      auth_method: payload.authMethod,
    });

    switch (payload.authMethod) {
      case 'oauth':
        initiateOAuth(
          platform,
          payload.title,
          payload.tags,
          payload.syncCadence,
          payload.advertiserId,
          // Google Ads asks for the manager rather than an account, but it answers the same
          // question: which of the accounts this login reaches the connection should use.
          payload.accountId ?? payload.loginCustomerId,
        );
        break;
      case 'system_user_token':
        connectTokenMutation.mutate({
          platform,
          title: payload.title,
          tags: payload.tags,
          token: payload.token!,
          syncCadence: payload.syncCadence,
          advertiserId: payload.advertiserId,
        });
        break;
      case 'service_account':
        connectServiceAccountMutation.mutate({
          platform,
          title: payload.title,
          tags: payload.tags,
          keyFileContent: payload.keyFileContent!,
          syncCadence: payload.syncCadence,
          loginCustomerId: payload.loginCustomerId,
          advertiserId: payload.advertiserId,
        });
        break;
    }
  };

  const handleToggle = (account: MediaConnector, checked: boolean) => {
    const newStatus: ConnectorStatus = checked ? 'connected' : 'disconnected';
    updateMutation.mutate(
      { id: account.id, status: newStatus },
      {
        onSuccess: () =>
          track('media_connector_status_changed', {
            platform,
            status: checked ? 'connected' : 'disconnected',
          }),
      },
    );
  };

  const handleEditSubmit = (input: {
    title: string;
    tags: string[];
    syncCadence: SyncCadence;
    dsaBeneficiary: string | null;
    dsaPayor: string | null;
    advertiserId: string | null;
    advertiserName: string | null;
  }) => {
    if (!editingAccount) return;
    updateMutation.mutate({ id: editingAccount.id, ...input });
  };

  const handleTest = (accountId: string) => testMutation.mutate(accountId);
  const handleSync = (connectorId: string) => triggerSyncMutation.mutate({ connectorId });
  const handleDeleteConfirm = () => {
    if (deletingAccount) {
      disconnectMutation.mutate(deletingAccount.id);
    }
  };

  const isBusy = isPending && accounts.length === 0;

  const testingAccountId = testMutation.isPending ? testMutation.variables : null;
  const togglingAccountId = updateMutation.isPending
    ? ((updateMutation.variables as { id: string } | undefined)?.id ?? null)
    : null;
  const syncingAccountId = triggerSyncMutation.isPending
    ? triggerSyncMutation.variables.connectorId
    : null;

  return {
    platform,
    platformLabel,
    platformIcon,

    accounts,
    filteredAccounts,
    syncStatusMap,
    isPending,
    isBusy,

    search,
    setSearch,
    statusFilter,
    setStatusFilter,

    dialogOpen,
    setDialogOpen,
    editingAccount,
    setEditingAccount,
    deletingAccount,
    setDeletingAccount,

    connectingPlatform,
    isConnectSubmitting: connectTokenMutation.isPending || connectServiceAccountMutation.isPending,
    handleAddAccount,

    handleToggle,
    handleEditSubmit,
    handleTest,
    handleSync,
    handleDeleteConfirm,
    isUpdating: updateMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
    testingAccountId,
    togglingAccountId,
    syncingAccountId,

    navigateBack: () => void navigate(ROUTES.KNOWLEDGE_HUB_MEDIA),
  };
}
